import type { MessageDescriptor } from 'react-intl'
import { defineMessage } from 'react-intl'
import { LOYALTY_OFFERS_HUB_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { LOYALTY_OFFERS_HUB_PARTNER_OFFERS_HASH } from 'loyalty/lib/loyaltyOffersHub/constants'
import { LUX_PLUS_MANAGE_ACCOUNT_HASH, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { CHAT_TRIP_PLANNER_NEW_PATH } from 'tripPlanner/config'

interface ActionRewardGroupConfig {
  order: number
  title: MessageDescriptor
}

export type LoyaltyActionRewardCTANav =
  | { kind: 'route'; to: string }
  | { kind: 'app-smart-link' }
  | { kind: 'component-render' }
  | { kind: 'whatsapp-channel' }

interface ActionRewardConfig {
  /** What user gets redirected to when clicking the action reward tile */
  ctaNav?: LoyaltyActionRewardCTANav
}

export enum SupportedActionRewardGroup {
  WAYS_TO_EARN = 'ways-to-earn',
  GET_STARTED = 'get-started',
}

const SUPPORTED_ACTION_REWARD_GROUPS = new Set<string>(Object.values(SupportedActionRewardGroup))

export const ACTION_REWARD_GROUP_CONFIGS: Record<SupportedActionRewardGroup, ActionRewardGroupConfig> = {
  [SupportedActionRewardGroup.GET_STARTED]: {
    order: 1,
    title: defineMessage({
      id: 'h8w2z59tDhina6yx00eS',
      defaultMessage: 'Get started',
      description: 'Heading for the Get started action rewards group on Société activities',
    }),
  },
  [SupportedActionRewardGroup.WAYS_TO_EARN]: {
    order: 2,
    title: defineMessage({
      id: 'ZvBlYqv2EoCbtm-ZsGLK',
      defaultMessage: 'More ways to earn',
      description: 'Heading for the Ways to earn action rewards group on Société activities',
    }),
  },
}

/**
 * Entrypoint of adding new action rewards:
 * - Add a member to SupportedActionReward and config at ACTION_REWARD_CONFIGS in this file
 * - Add a member to SupportedActionRewardGroup and config at ACTION_REWARD_GROUP_CONFIGS in this file
 * - Add icon in LoyaltyActionRewardsIcon.tsx
 * - (Optional) Add visibility condition in useLoyaltyActionRewards
 * - (Optional) Add earn calculation metadata resolver in getLoyaltyActionRewardEarnRequest
 */
export enum SupportedActionReward {
  OFFER_SHARED = 'OfferShared',
  PARTNER_ACCOUNT_EARNED = 'PartnerAccountEarned',
  MOBILE_WIDGET_ADDED = 'MobileWidgetAdded',
  LUX_PLUS_BACKUP_PAYMENT_METHOD_ADDED = 'LuxPlusBackupPaymentMethodAdded',
  /** @NOTE - Re-enable once the staggered renewal bonus UI is more figured out and fixed. */
  // LUX_PLUS_RENEWAL = 'LuxPlusRenewal',
  TRIP_PLANNER_USED = 'TripPlannerUsed',
  PHONE_NUMBER_ADDED = 'PhoneNumberAdded',
  DATE_OF_BIRTH_ADDED = 'DateOfBirthAdded',
  BUSINESS_DETAILS_ADDED = 'BusinessDetailsAdded',
  MOBILE_APP_DOWNLOADED = 'MobileAppDownloaded',
  SUBSCRIBE_TO_COMMUNICATIONS = 'SubscribeToCommunications500',
  WHATS_APP_CHANNEL_JOINED = 'WhatsAppChannelJoined',
}

const SUPPORTED_ACTION_REWARDS = new Set<string>(Object.values(SupportedActionReward))

export const ACTION_REWARD_CONFIGS = {
  [SupportedActionReward.OFFER_SHARED]: {
    ctaNav: { kind: 'route', to: '/' },
  },
  [SupportedActionReward.TRIP_PLANNER_USED]: {
    ctaNav: { kind: 'route', to: CHAT_TRIP_PLANNER_NEW_PATH },
  },
  [SupportedActionReward.MOBILE_WIDGET_ADDED]: {
    ctaNav: { kind: 'app-smart-link' },
  },
  [SupportedActionReward.PARTNER_ACCOUNT_EARNED]: {
    ctaNav: { kind: 'route', to: `/${LOYALTY_OFFERS_HUB_PAGE}#${LOYALTY_OFFERS_HUB_PARTNER_OFFERS_HASH}` },
  },
  // [SupportedActionReward.LUX_PLUS_RENEWAL]: {
  //   ctaNav: { kind: 'route', to: `/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}` },
  // },
  [SupportedActionReward.LUX_PLUS_BACKUP_PAYMENT_METHOD_ADDED]: {
    ctaNav: { kind: 'route', to: `/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}${LUX_PLUS_MANAGE_ACCOUNT_HASH}` },
  },
  [SupportedActionReward.PHONE_NUMBER_ADDED]: {
    ctaNav: { kind: 'route', to: '/account/details' },
  },
  [SupportedActionReward.DATE_OF_BIRTH_ADDED]: {
    ctaNav: { kind: 'route', to: '/account/details' },
  },
  [SupportedActionReward.BUSINESS_DETAILS_ADDED]: {
    ctaNav: { kind: 'route', to: '/account/details' },
  },
  [SupportedActionReward.MOBILE_APP_DOWNLOADED]: {
    ctaNav: { kind: 'app-smart-link' },
  },
  [SupportedActionReward.SUBSCRIBE_TO_COMMUNICATIONS]: {
    ctaNav: { kind: 'component-render' },
  },
  [SupportedActionReward.WHATS_APP_CHANNEL_JOINED]: {
    // Points are granted manually for now; joining a channel can't be verified
    ctaNav: { kind: 'whatsapp-channel' },
  },
} as const satisfies Record<SupportedActionReward, ActionRewardConfig>

export type ComponentRenderActionReward = {
  [ActionName in SupportedActionReward]: Extract<
    (typeof ACTION_REWARD_CONFIGS)[ActionName]['ctaNav'],
    { kind: 'component-render' }
  > extends never ? never : ActionName
}[SupportedActionReward]

// Type narrowing
export function isComponentRenderActionReward(
  actionName: SupportedActionReward,
): actionName is ComponentRenderActionReward {
  return ACTION_REWARD_CONFIGS[actionName].ctaNav?.kind === 'component-render'
}

function isSupportedActionReward(actionName: string): actionName is SupportedActionReward {
  return SUPPORTED_ACTION_REWARDS.has(actionName)
}

function isSupportedActionRewardGroup(groupType: string): groupType is SupportedActionRewardGroup {
  return SUPPORTED_ACTION_REWARD_GROUPS.has(groupType)
}

function hasSupportedActionRewardRepeatCap(reward: App.LoyaltyActionReward): boolean {
  if (!reward.allowRepeat) return true
  if (reward.maxRepeat === undefined || reward.maxRepeat >= 1) return true
  return false
}

export type SupportedLoyaltyActionReward = App.LoyaltyActionReward & {
  actionName: SupportedActionReward
  groupType: SupportedActionRewardGroup
}

export function isSupportedLoyaltyActionReward(
  reward: App.LoyaltyActionReward,
): reward is SupportedLoyaltyActionReward {
  return reward.groupType != null
    && isSupportedActionRewardGroup(reward.groupType)
    && isSupportedActionReward(reward.actionName)
    && hasSupportedActionRewardRepeatCap(reward)
}
